/**
 * Game invites — when a friend wants to play, they create an invite
 * targeted at a specific uid. The recipient sees a popup notification
 * + a Recent Requests entry on Home until they Join, Decline, or it
 * expires. Backed by Firestore so a recipient who's currently offline
 * still sees the invite next time they open the app.
 *
 * Schema (Firestore `gameInvites/{inviteId}`):
 *   { fromUid, fromName, fromAvatar,
 *     toUid, toName, toAvatar,
 *     roomCode,                       // the room the inviter created
 *     mode: 'live' | 'async',
 *     status: 'pending' | 'accepted' | 'declined' | 'expired',
 *     createdAt, expiresAt,
 *     turnTimeoutHours?               // for async mode
 *   }
 */

import { db } from './firebase';
import {
  collection, addDoc, updateDoc, doc, query, where, onSnapshot, serverTimestamp,
  Timestamp, type Unsubscribe,
} from 'firebase/firestore';

export type GameInviteMode = 'live' | 'async';
export type GameInviteStatus = 'pending' | 'accepted' | 'declined' | 'expired';

export interface GameInvite {
  id: string;
  fromUid: string;
  fromName: string;
  fromAvatar: string;
  toUid: string;
  toName: string;
  toAvatar: string;
  roomCode: string;
  mode: GameInviteMode;
  status: GameInviteStatus;
  createdAtMs: number;
  expiresAtMs: number;
  turnTimeoutHours?: number;
}

/**
 * Send an invite. The room must already exist (caller creates it via
 * createRoom first). Default expiry is 1 hour for live games; async
 * invites expire after 7 days.
 */
export async function sendGameInvite(params: {
  fromUid: string;
  fromName: string;
  fromAvatar: string;
  toUid: string;
  toName: string;
  toAvatar: string;
  roomCode: string;
  mode?: GameInviteMode;
  turnTimeoutHours?: number;
}): Promise<string | null> {
  if (!db) return null;
  const mode: GameInviteMode = params.mode || 'live';
  const ttlMs = mode === 'async' ? 7 * 24 * 60 * 60 * 1000 : 60 * 60 * 1000;
  try {
    const ref = await addDoc(collection(db, 'gameInvites'), {
      fromUid: params.fromUid,
      fromName: params.fromName,
      fromAvatar: params.fromAvatar,
      toUid: params.toUid,
      toName: params.toName,
      toAvatar: params.toAvatar,
      roomCode: params.roomCode,
      mode,
      status: 'pending',
      createdAt: serverTimestamp(),
      expiresAt: Timestamp.fromMillis(Date.now() + ttlMs),
      turnTimeoutHours: params.turnTimeoutHours || null,
    });
    return ref.id;
  } catch (e) {
    console.warn('[gameInvitesService] sendGameInvite failed:', (e as any)?.message || e);
    return null;
  }
}

export async function setInviteStatus(inviteId: string, status: GameInviteStatus): Promise<void> {
  if (!db || !inviteId) return;
  try {
    await updateDoc(doc(db, 'gameInvites', inviteId), {
      status,
      respondedAt: serverTimestamp(),
    });
  } catch (e) {
    console.warn('[gameInvitesService] setInviteStatus failed:', (e as any)?.message || e);
  }
}

/**
 * Live subscription to invites where I'm the recipient. Fires once
 * immediately + on every change. Only emits 'pending' invites that
 * haven't expired yet — accepted/declined drop off the list.
 */
export function subscribeIncomingInvites(uid: string, onChange: (rows: GameInvite[]) => void): Unsubscribe {
  if (!db || !uid) return () => {};
  try {
    const ref = collection(db, 'gameInvites');
    const qq = query(ref, where('toUid', '==', uid), where('status', '==', 'pending'));
    return onSnapshot(qq, snap => {
      const now = Date.now();
      const out: GameInvite[] = [];
      snap.forEach(d => {
        const data = d.data() as any;
        const expiresAtMs = data.expiresAt?.toMillis ? data.expiresAt.toMillis() : 0;
        if (expiresAtMs && expiresAtMs < now) return; // expired — server-side cron should clean these up
        out.push({
          id: d.id,
          fromUid: data.fromUid,
          fromName: data.fromName,
          fromAvatar: data.fromAvatar,
          toUid: data.toUid,
          toName: data.toName,
          toAvatar: data.toAvatar,
          roomCode: data.roomCode,
          mode: data.mode || 'live',
          status: data.status,
          createdAtMs: data.createdAt?.toMillis ? data.createdAt.toMillis() : 0,
          expiresAtMs,
          turnTimeoutHours: data.turnTimeoutHours || undefined,
        });
      });
      // Newest first — older invites should fade in importance.
      out.sort((a, b) => b.createdAtMs - a.createdAtMs);
      onChange(out);
    }, err => {
      console.warn('[gameInvitesService] subscribeIncomingInvites snapshot error:', err?.message || err);
    });
  } catch (e) {
    console.warn('[gameInvitesService] subscribeIncomingInvites failed:', (e as any)?.message || e);
    return () => {};
  }
}
